/**
 * Section 05 — Assessment Results by Cybersecurity Domain.
 *
 * Layout:
 *   • One narrative intro paragraph (no DOMAINS COVERED list).
 *   • First domain is rendered on the same page immediately if space
 *     permits, otherwise it starts on the next page.
 *   • Each subsequent domain renders on its own page.
 *
 * Per-domain page contents:
 *   • Eyebrow header (SECTION 05[: / (cont'd):] handled by drawSectionTitle).
 *   • Domain name (large navy heading).
 *   • Domain Overview narrative (band-aware, csvDomainNarrative).
 *   • Metrics block (tabular, report-oriented):
 *       Domain Rating         (label only — never a numeric score)
 *       Controls Evaluated
 *       Implemented
 *       Partially Implemented
 *       Not Implemented
 *       Findings Logged
 *   • Reviewer Observations.
 *   • Key Findings (top 3 by risk).
 *   • Recommended Improvements (top 3 distinct).
 *
 * Inputs come from existing RPCs:
 *   - calculate_csv_domain_scores
 *   - reviewer_get_roadmap
 *   - reviewer_get_csv_domain_implementation_breakdown (additive)
 */
import type jsPDF from "jspdf";
import { PAGE_MARGIN, REPORT_THEME, type RGB } from "./theme";
import { drawRunningHeader, drawPageFooter, drawSectionTitle } from "./pageChrome";
import {
  bandFromScore,
  csvDomainNarrative,
  type MaturityBand,
} from "@/reports/telecom/domainNarratives";
import { DOMAIN_ORDER, type CsvDomainScoreRow } from "./renderCsvDomainRatings";
import { REVIEWER_CONTROL_GUIDANCE } from "@/configs/controlGuidance/reviewerControlGuidance";
import {
  INFRASTRUCTURE_COVERAGE_NOTE_PDF,
  INFRASTRUCTURE_PARTIAL_BADGE,
} from "@/reports/telecom/infrastructureCoverageNote";

export interface RoadmapFinding {
  control_code?: string | null;
  finding_title?: string | null;
  risk_level?: string | null;
  priority_level?: string | null;
  recommended_action?: string | null;
  recommended_measures?: string | null;
  csv_domain?: string | null;
}

export interface ControlResultRow {
  csv_domain?: string | null;
  nist_function?: string | null;
  control_code: string;
  question_id?: number | null;
  question_title?: string | null;
  reviewer_score?: number | null;
  reviewer_notes?: string | null;
  recommended_action?: string | null;
  finding_title?: string | null;
  risk_level?: string | null;
}

export interface DomainImplementationBreakdown {
  domain_name: string;
  implemented: number;
  partial: number;
  not_implemented: number;
}

export interface InfrastructureScopeForReport {
  telecom_service_model?: string[] | null;
  components_present?: string[] | null;
  components_assessed?: string[] | null;
  scope_notes?: string | null;
}

export interface DomainResultsContext {
  scores: CsvDomainScoreRow[];
  roadmap: RoadmapFinding[];
  breakdown: DomainImplementationBreakdown[];
  /** Per-(question, control) reviewer rows used by the tabular Section 05 layout. */
  controlResults?: ControlResultRow[];
  clientOrgName: string;
  methodology?: string | null;
  frameworks?: string[] | null;
  /** First page number that this section will own. */
  startPageNumber: number;
  /** Phase 1.8 — Infrastructure scope disclosure (informational only). */
  scope?: InfrastructureScopeForReport | null;
}

const BAND_LABEL: Record<MaturityBand, string> = {
  Minimal: "Minimal",
  Adequate: "Adequate",
  Strong: "Strong",
  Extensive: "Extensive",
  Unrated: "Not Assessed",
};

const BAND_TEXT_COLOR: Record<MaturityBand, RGB> = {
  Minimal:   [220, 38, 38],
  Adequate:  [217, 119, 6],
  Strong:    [22, 163, 74],
  Extensive: [37, 99, 235],
  Unrated:   [110, 110, 110],
};

const RISK_RANK: Record<string, number> = {
  critical: 1, high: 2, moderate: 3, medium: 3, low: 4,
};

const RISK_COLOR: Record<string, RGB> = {
  critical: [153, 27, 27],
  high:     [220, 38, 38],
  moderate: [217, 119, 6],
  medium:   [217, 119, 6],
  low:      [148, 163, 184],
};

/* ──────────────────────────── Status helpers ──────────────────────────── */

type StatusKey = "implemented" | "partial" | "not_implemented" | "unrated";

interface StatusStyle {
  label: string;
  fill: RGB;
  text: RGB;
  border: RGB;
}

function statusFromScore(score?: number | null): StatusKey {
  if (score == null) return "unrated";
  if (score >= 75) return "implemented";
  if (score <= 25) return "not_implemented";
  return "partial";
}

const STATUS_STYLES: Record<StatusKey, StatusStyle> = {
  implemented: {
    label: "Implemented",
    fill: [220, 245, 226],
    text: [21, 128, 61],   // green-700 — clearly green
    border: [22, 163, 74],
  },
  partial: {
    label: "Partially Implemented",
    fill: [255, 237, 213],
    text: [234, 88, 12],   // orange-600 — obviously orange
    border: [234, 88, 12],
  },
  not_implemented: {
    label: "Not Implemented",
    fill: [254, 226, 226],
    text: [153, 27, 27],   // red-800 — dark red
    border: [185, 28, 28],
  },
  unrated: {
    label: "Not Rated",
    fill: [243, 244, 246],
    text: [75, 85, 99],
    border: [156, 163, 175],
  },
};

/* ──────────────────────────── Entry point ─────────────────────────────── */

/** Section 05 entry point. Returns the last page number used. */
export function renderDomainResults(doc: jsPDF, ctx: DomainResultsContext): number {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Build ordered domain list using the canonical order.
  const scoreMap = new Map<string, CsvDomainScoreRow>(
    (ctx.scores ?? []).map((s) => [s.domain_name, s]),
  );
  const ordered = [
    ...DOMAIN_ORDER.filter((d) => scoreMap.has(d)),
    ...(ctx.scores ?? []).map((s) => s.domain_name).filter((d) => !DOMAIN_ORDER.includes(d)),
  ];
  const domains = ordered.length > 0 ? ordered : DOMAIN_ORDER;

  const controlRows = ctx.controlResults ?? [];
  const findingsAll = ctx.roadmap ?? [];

  // ── Section opener (narrative intro) ──
  doc.addPage();
  let page = ctx.startPageNumber;
  drawRunningHeader(doc, {
    clientName: ctx.clientOrgName,
    frameworks: ctx.frameworks ?? null,
    methodology: ctx.methodology ?? null,
  });

  let y = drawSectionTitle(doc, {
    sectionNumber: "05",
    title: "Assessment Results by Cybersecurity Domain",
  });

  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  const intro =
    "This section provides detailed results for each cybersecurity domain assessed, including maturity ratings, identified findings, associated risks, and prioritized recommendations. Together, these results highlight strengths, improvement opportunities, and the actions required to strengthen overall cyber resilience.";
  const introLines = doc.splitTextToSize(intro, pageW - PAGE_MARGIN * 2);
  doc.text(introLines, PAGE_MARGIN, y, { lineHeightFactor: 1.4 } as any);
  y += introLines.length * 13 + 10;

  drawPageFooter(doc, page);

  // Domains flow inline whenever the next block fits in the remaining
  // space. If the full domain does not fit but its header/table start does,
  // start it inline and let the table continue cleanly on the next page.
  // This keeps smaller domains packed and allows Security Operations to
  // begin immediately below Identity-Centric Security when there is room.
  const bottomLimit = pageH - 70;
  const DOMAIN_GAP = 18;

  for (let i = 0; i < domains.length; i++) {
    const name = domains[i];



    const domainControls = controlRows.filter(
      (r) => (r.csv_domain ?? "").trim() === name,
    );
    const domainFindings = findingsAll.filter((f) => (f.csv_domain ?? "").trim() === name);

    let startY: number | null;
    let nextPage: number;

    if (i === 0) {
      startY = y;
      nextPage = page;
    } else {
      const blockH = measureDomainBlockHeight(doc, {
        domain: name,
        score: scoreMap.get(name) ?? null,
        controls: domainControls,
        findings: domainFindings,
        pageW,
      });
      const starterH = measureDomainStarterHeight(doc, {
        domain: name,
        score: scoreMap.get(name) ?? null,
        controls: domainControls,
        findings: domainFindings,
        pageW,
      });
      const remaining = bottomLimit - y - DOMAIN_GAP;
      if (blockH <= remaining || starterH <= remaining) {
        // Fits inline below the previous domain — continue same page.
        startY = y + DOMAIN_GAP;
        nextPage = page;
      } else {
        // Start a fresh page.
        page += 1;
        doc.addPage();
        drawRunningHeader(doc, {
          clientName: ctx.clientOrgName,
          frameworks: ctx.frameworks ?? null,
          methodology: ctx.methodology ?? null,
        });
        startY = drawSectionTitle(doc, {
          sectionNumber: "05",
          title: "Assessment Results by Cybersecurity Domain",
          continued: true,
        }) + 10;
        nextPage = page;
      }
    }

    const res = renderDomainPage(doc, {
      domain: name,
      score: scoreMap.get(name) ?? null,
      findings: domainFindings,
      controls: domainControls,
      clientOrgName: ctx.clientOrgName,
      methodology: ctx.methodology ?? null,
      frameworks: ctx.frameworks ?? null,
      pageNumber: nextPage,
      pageW,
      pageH,
      startY,
    });
    page = res.page;
    y = res.endY;

    // Phase 1.8 — Infrastructure scope page renders AFTER the Infrastructure
    // Security domain so the domain itself stays adjacent to Data Protection.
    if (name === "Infrastructure Security" && hasInfrastructureScope(ctx.scope)) {
      page += 1;
      renderInfrastructureScopePage(doc, {
        scope: ctx.scope!,
        clientOrgName: ctx.clientOrgName,
        methodology: ctx.methodology ?? null,
        frameworks: ctx.frameworks ?? null,
        pageNumber: page,
      });
      y = bottomLimit + 1;
    }
  }

  return page;
}

/* ──────────────────────────── Per-domain page ─────────────────────────── */

interface DomainPageArgs {
  domain: string;
  score: CsvDomainScoreRow | null;
  findings: RoadmapFinding[];
  controls: ControlResultRow[];
  clientOrgName: string;
  methodology: string | null;
  frameworks: string[] | null;
  pageNumber: number;
  pageW: number;
  pageH: number;
  startY?: number | null;
}

interface DomainPageResult { page: number; endY: number; }

function renderDomainPage(doc: jsPDF, args: DomainPageArgs): DomainPageResult {
  const { domain, score, findings, controls, clientOrgName, methodology, frameworks, pageW, pageH } = args;
  const innerW = pageW - PAGE_MARGIN * 2;
  const bottomLimit = pageH - 70;
  let page = args.pageNumber;

  // First domain reuses the opener page (startY provided); others get a fresh page.
  let y: number;
  if (args.startY != null) {
    y = args.startY;
  } else {
    doc.addPage();
    drawRunningHeader(doc, {
      clientName: clientOrgName,
      frameworks,
      methodology,
    });
    y = drawSectionTitle(doc, {
      sectionNumber: "05",
      title: "Assessment Results by Cybersecurity Domain",
      continued: true,
    });
    y += 10;
  }

  // ── Domain header band (compact: name + rating chip + short exec paragraph) ──
  const scoreVal = score?.average_score ?? null;
  const controlsEvaluated = Math.max(Number(score?.controls_evaluated ?? 0), controls.length);
  const band = bandFromScore(scoreVal, controlsEvaluated);

  const execText = buildExecutiveAssessment(domain, band, findings, scoreVal, controlsEvaluated);
  const headerPadX = 12;
  const headerPadY = 10;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  // No truncation — narrative expands the card dynamically.
  const execLines = doc.splitTextToSize(execText, innerW - headerPadX * 2) as string[];
  const execLineH = 11;
  const titleRowH = 20;
  const headerH = headerPadY + titleRowH + 6 + execLines.length * execLineH + headerPadY - 2;

  drawContainer(doc, PAGE_MARGIN, y, innerW, headerH, REPORT_THEME.surfaceAlt.rgb, REPORT_THEME.border.rgb);
  doc.setFillColor(...REPORT_THEME.accent.rgb);
  doc.rect(PAGE_MARGIN, y, 4, headerH, "F");

  // Domain title (smaller)
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(domain, PAGE_MARGIN + headerPadX, y + headerPadY + 13);

  // Rating chip (right-aligned, vertically centered on title row)
  let chipText = BAND_LABEL[band];
  let chipColor = BAND_TEXT_COLOR[band];
  // Infrastructure Security: when controls exist but the aggregate score is
  // null, surface partial-coverage status instead of a flat "Not Assessed".
  // Scoring engine change is deferred to a separate ticket.
  if (
    domain === "Infrastructure Security" &&
    band === "Unrated" &&
    controls.length > 0
  ) {
    chipText = INFRASTRUCTURE_PARTIAL_BADGE;
    chipColor = [217, 119, 6]; // amber-600
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  const chipPadX = 8;
  const chipH = 15;
  const chipW = doc.getTextWidth(chipText) + chipPadX * 2;
  const chipX = PAGE_MARGIN + innerW - headerPadX - chipW;
  const chipY = y + headerPadY + 1;
  doc.setFillColor(...chipColor);
  doc.roundedRect(chipX, chipY, chipW, chipH, chipH / 2, chipH / 2, "F");
  doc.setTextColor(255, 255, 255);
  doc.text(chipText, chipX + chipPadX, chipY + 10.5);

  // Exec paragraph (immediately below title — no separate OVERALL RATING label)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  doc.text(execLines, PAGE_MARGIN + headerPadX, y + headerPadY + titleRowH + 6 + 8, { lineHeightFactor: 1.35 } as any);

  y += headerH + 10;

  // ── Control results table ──
  const seen = new Set<string>();
  const tableRows: ControlResultRow[] = [];
  for (const r of controls) {
    const key = (r.control_code ?? "").toUpperCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    tableRows.push(r);
  }
  tableRows.sort((a, b) =>
    (a.control_code ?? "").localeCompare(b.control_code ?? "", undefined, { numeric: true }),
  );

  if (tableRows.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10.5);
    doc.setTextColor(...REPORT_THEME.textMuted.rgb);
    doc.text(
      "No reviewer-rated controls were recorded for this domain.",
      PAGE_MARGIN,
      y + 14,
    );
    drawPageFooter(doc, page);
    return { page, endY: y + 24 };
  }

  // Column layout — narrow status column; redistribute width to text-heavy columns.
  const COLS = [
    { key: "control",         header: "Control",             weight: 0.10 },
    { key: "description",     header: "Control Description", weight: 0.28 },
    { key: "status",          header: "Status",              weight: 0.16 },
    { key: "observations",    header: "Assessment Notes",    weight: 0.23 },
    { key: "recommendations", header: "Recommendations",     weight: 0.23 },
  ] as const;
  const totalW = COLS.reduce((a, c) => a + c.weight, 0);
  const colW = COLS.map((c) => (c.weight / totalW) * innerW);
  const colX: number[] = [];
  {
    let cx = PAGE_MARGIN;
    for (let i = 0; i < colW.length; i++) { colX.push(cx); cx += colW[i]; }
  }

  const CELL_PAD_X = 5;
  const CELL_PAD_Y = 6;
  const LINE_H = 10.5;
  const HEADER_FONT = 8.5;
  const BODY_FONT = 8;
  const STATUS_FONT = 8.5;

  // Lighter blue for column headers
  const HEADER_FILL: RGB = [78, 116, 160];
  const GRID_COLOR: RGB = [180, 192, 210];

  const wrapCell = (text: string, maxWidth: number): string[] => {
    const inner = maxWidth - CELL_PAD_X * 2;
    const paras = String(text ?? "").split(/\n+/);
    const out: string[] = [];
    for (const para of paras) {
      const words = para.split(/\s+/).filter(Boolean);
      if (words.length === 0) { out.push(""); continue; }
      let cur = "";
      for (const w of words) {
        const t = cur ? cur + " " + w : w;
        if (doc.getTextWidth(t) <= inner) {
          cur = t;
        } else {
          if (cur) out.push(cur);
          if (doc.getTextWidth(w) > inner) {
            const parts = doc.splitTextToSize(w, inner) as string[];
            out.push(...parts.slice(0, -1));
            cur = parts[parts.length - 1] ?? "";
          } else {
            cur = w;
          }
        }
      }
      if (cur) out.push(cur);
    }
    return out.length === 0 ? [""] : out;
  };

  const drawTableHeader = (atY: number): number => {
    // Pre-wrap header labels using the same metrics as body
    doc.setFont("helvetica", "bold");
    doc.setFontSize(HEADER_FONT);
    const wrapped = COLS.map((c, i) => wrapCell(c.header, colW[i]));
    const maxLines = Math.max(...wrapped.map((w) => w.length));
    const headerH = maxLines * 11 + 10;

    doc.setFillColor(...HEADER_FILL);
    doc.rect(PAGE_MARGIN, atY, innerW, headerH, "F");
    // Vertical dividers in header
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.5);
    for (let i = 1; i < COLS.length; i++) {
      doc.line(colX[i], atY, colX[i], atY + headerH);
    }
    doc.setTextColor(255, 255, 255);
    for (let i = 0; i < COLS.length; i++) {
      const lines = wrapped[i];
      const startY = atY + (headerH - lines.length * 11) / 2 + 8;
      lines.forEach((ln, li) => {
        doc.text(ln, colX[i] + CELL_PAD_X, startY + li * 11);
      });
    }
    return atY + headerH;
  };

  let currentTableTop = y;
  let currentRowStartY = y;

  const newDomainPage = (): number => {
    drawPageFooter(doc, page);
    page += 1;
    doc.addPage();
    drawRunningHeader(doc, { clientName: clientOrgName, frameworks, methodology });
    let ny = drawSectionTitle(doc, {
      sectionNumber: "05",
      title: "Assessment Results by Cybersecurity Domain",
      continued: true,
    });
    ny += 8;
    doc.setFillColor(...REPORT_THEME.surfaceAlt.rgb);
    doc.rect(PAGE_MARGIN, ny, innerW, 20, "F");
    doc.setFillColor(...REPORT_THEME.accent.rgb);
    doc.rect(PAGE_MARGIN, ny, 4, 20, "F");
    doc.setTextColor(...REPORT_THEME.navy.rgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(`${domain} (continued)`, PAGE_MARGIN + 12, ny + 14);
    ny += 28;
    currentTableTop = ny;
    currentRowStartY = drawTableHeader(ny);
    return currentRowStartY;
  };

  // Initial header
  y = drawTableHeader(y);
  currentRowStartY = y;

  // Data rows
  let zebra = false;
  for (let idx = 0; idx < tableRows.length; idx++) {
    const r = tableRows[idx];
    const code = r.control_code ?? "—";
    const guidance = REVIEWER_CONTROL_GUIDANCE[code];
    const ctrlTitle = guidance?.title ?? (r.question_title ?? "");
    const ctrlDesc = guidance?.reviewer_guidance ?? "";
    const description = ctrlTitle && ctrlDesc
      ? `${ctrlTitle}\n${ctrlDesc}`
      : (ctrlTitle || ctrlDesc || (r.question_title ?? "—"));
    const status = statusFromScore(r.reviewer_score);
    const statusStyle = STATUS_STYLES[status];
    const observations = (r.reviewer_notes ?? "").trim() || "—";
    const recRaw = (r.recommended_action ?? "").trim();
    const recommendations =
      status === "implemented" ? "—" : (recRaw || "—");

    // No artificial truncation — cells expand vertically to fit content so
    // observations / recommendations never trail off with an ellipsis.
    doc.setFont("helvetica", "bold"); doc.setFontSize(BODY_FONT);
    const ctrlLines = wrapCell(code, colW[0]);
    // Title is rendered BOLD — must be wrapped with bold metrics or it
    // overflows into the adjacent Status column.
    doc.setFont("helvetica", "bold"); doc.setFontSize(BODY_FONT);
    const descTitleLines = ctrlTitle ? wrapCell(ctrlTitle, colW[1]) : [];
    doc.setFont("helvetica", "normal");
    const descBodyLines = ctrlDesc ? wrapCell(ctrlDesc, colW[1]) : [];
    const descFallbackLines = (guidance && ctrlTitle && ctrlDesc)
      ? []
      : wrapCell(description, colW[1]);
    const descLinesCount = (guidance && ctrlTitle && ctrlDesc)
      ? descTitleLines.length + descBodyLines.length
      : descFallbackLines.length;

    // Status: force one-word-per-line wrapping when the label doesn't fit on
    // a single line — never break a word mid-character.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(STATUS_FONT);
    const statusMaxInner = colW[2] - CELL_PAD_X * 2;
    const statusLines: string[] = (() => {
      const label = statusStyle.label;
      // Single-word labels (Implemented) must never split across lines.
      if (status === "implemented") return [label];
      if (status === "partial" || status === "not_implemented") {
        return label.split(/\s+/).filter(Boolean);
      }
      if (doc.getTextWidth(label) <= statusMaxInner) return [label];
      const words = label.split(/\s+/).filter(Boolean);
      const oversized = words.some((w) => doc.getTextWidth(w) > statusMaxInner);
      return oversized ? (doc.splitTextToSize(label, statusMaxInner) as string[]) : words;
    })();

    doc.setFont("helvetica", "normal"); doc.setFontSize(BODY_FONT);
    const obsLines = wrapCell(observations, colW[3]);
    const recLines = wrapCell(recommendations, colW[4]);

    // Include status line count so multi-line labels are never clipped by
    // the row divider.
    const contentLines = Math.max(
      ctrlLines.length,
      descLinesCount,
      statusLines.length,
      obsLines.length,
      recLines.length,
    );
    const rowH = Math.max(28, contentLines * LINE_H + CELL_PAD_Y * 2);

    if (y + rowH > bottomLimit) {
      doc.setDrawColor(...GRID_COLOR);
      doc.setLineWidth(0.6);
      for (let i = 1; i < COLS.length; i++) {
        doc.line(colX[i], currentRowStartY, colX[i], y);
      }
      doc.setLineWidth(0.8);
      doc.rect(PAGE_MARGIN, currentTableTop, innerW, y - currentTableTop);
      y = newDomainPage();
      zebra = false;
    }

    // Zebra background — inset 1pt top & bottom so neighbouring row
    // dividers (drawn at y and y+rowH) are never painted over by the
    // next row's fill. This was the root cause of the intermittent
    // "missing horizontal borders" reported across Governance / Risk
    // Management / Security Operations control tables.
    if (zebra) {
      doc.setFillColor(247, 249, 252);
      doc.rect(PAGE_MARGIN, y + 1, innerW, rowH - 2, "F");
    }
    // Bottom divider
    doc.setDrawColor(...GRID_COLOR);
    doc.setLineWidth(0.8);
    doc.line(PAGE_MARGIN, y + rowH, PAGE_MARGIN + innerW, y + rowH);
    // Top divider — guarantees the separator is visible even when the
    // previous row had no zebra fill or this is the first row after a
    // page break / continuation header.
    doc.line(PAGE_MARGIN, y, PAGE_MARGIN + innerW, y);

    const baseY = y + CELL_PAD_Y + LINE_H - 1;

    // Col 1 — Control code
    doc.setFont("helvetica", "bold");
    doc.setFontSize(BODY_FONT);
    doc.setTextColor(...REPORT_THEME.navy.rgb);
    doc.text(ctrlLines, colX[0] + CELL_PAD_X, baseY, { lineHeightFactor: 1.2 } as any);

    // Col 2 — Description
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
    if (guidance && ctrlTitle && ctrlDesc) {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...REPORT_THEME.navy.rgb);
      doc.text(descTitleLines, colX[1] + CELL_PAD_X, baseY, { lineHeightFactor: 1.2 } as any);
      const titleBlock = descTitleLines.length * LINE_H;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
      doc.text(descBodyLines, colX[1] + CELL_PAD_X, baseY + titleBlock, { lineHeightFactor: 1.2 } as any);
    } else {
      doc.text(descFallbackLines, colX[1] + CELL_PAD_X, baseY, { lineHeightFactor: 1.2 } as any);
    }

    // Col 3 — Color-coded status text, centered horizontally and vertically
    // within the column for a clean, scannable middle alignment.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(STATUS_FONT);
    doc.setTextColor(...statusStyle.text);
    const statusBlockH = statusLines.length * LINE_H;
    const statusTopY = y + (rowH - statusBlockH) / 2 + LINE_H - 2;
    const statusCenterX = colX[2] + colW[2] / 2;
    doc.text(statusLines, statusCenterX, statusTopY, { align: "center", lineHeightFactor: 1.2 } as any);

    // Col 4 — Observations
    doc.setFont("helvetica", "normal");
    doc.setFontSize(BODY_FONT);
    doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
    doc.text(obsLines, colX[3] + CELL_PAD_X, baseY, { lineHeightFactor: 1.2 } as any);

    // Col 5 — Recommendations
    doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
    doc.text(recLines, colX[4] + CELL_PAD_X, baseY, { lineHeightFactor: 1.2 } as any);

    y += rowH;
    zebra = !zebra;
  }

  // Finish: vertical dividers + outer border for the final table block
  doc.setDrawColor(...GRID_COLOR);
  doc.setLineWidth(0.6);
  for (let i = 1; i < COLS.length; i++) {
    doc.line(colX[i], currentRowStartY, colX[i], y);
  }
  doc.setLineWidth(0.8);
  doc.rect(PAGE_MARGIN, currentTableTop, innerW, y - currentTableTop);

  drawPageFooter(doc, page);
  return { page, endY: y };
}


/**
 * Conservative measurement of the total height a domain block will occupy
 * if rendered starting at the top of a clean region. Mirrors the rendering
 * math in `renderDomainPage` so the orchestrator can pack small domains
 * onto the same page when space allows.
 */
function measureDomainBlockHeight(
  doc: jsPDF,
  args: {
    domain: string;
    score: CsvDomainScoreRow | null;
    controls: ControlResultRow[];
    findings: RoadmapFinding[];
    pageW: number;
  },
): number {
  const innerW = args.pageW - PAGE_MARGIN * 2;

  const scoreVal = args.score?.average_score ?? null;
  const controlsEvaluated = Number(args.score?.controls_evaluated ?? args.controls.length);
  const band = bandFromScore(scoreVal, controlsEvaluated);
  const execText = buildExecutiveAssessment(args.domain, band, args.findings, scoreVal, controlsEvaluated);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const execLines = (doc.splitTextToSize(execText, innerW - 24) as string[]).slice(0, 3);
  const headerBandH = 10 + 20 + 6 + execLines.length * 11 + 8;

  if ((args.controls?.length ?? 0) === 0) {
    return headerBandH + 10 + 24;
  }

  const weights = [0.11, 0.29, 0.12, 0.25, 0.23];
  const sumW = weights.reduce((a, b) => a + b, 0);
  const colW = weights.map((w) => (w / sumW) * innerW);

  const CELL_PAD_X = 5;
  const CELL_PAD_Y = 6;
  const LINE_H = 10.5;
  const BODY_FONT = 8;
  const STATUS_FONT = 8.5;

  const wrap = (text: string, colWidth: number): string[] => {
    const inner = colWidth - CELL_PAD_X * 2;
    const paras = String(text ?? "").split(/\n+/);
    const out: string[] = [];
    for (const para of paras) {
      const words = para.split(/\s+/).filter(Boolean);
      if (words.length === 0) { out.push(""); continue; }
      let cur = "";
      for (const w of words) {
        const t = cur ? cur + " " + w : w;
        if (doc.getTextWidth(t) <= inner) cur = t;
        else {
          if (cur) out.push(cur);
          if (doc.getTextWidth(w) > inner) {
            const parts = doc.splitTextToSize(w, inner) as string[];
            out.push(...parts.slice(0, -1));
            cur = parts[parts.length - 1] ?? "";
          } else cur = w;
        }
      }
      if (cur) out.push(cur);
    }
    return out.length === 0 ? [""] : out;
  };

  const tableHeaderH = 11 + 10;

  const seen = new Set<string>();
  const rows: ControlResultRow[] = [];
  for (const r of args.controls) {
    const k = (r.control_code ?? "").toUpperCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    rows.push(r);
  }

  let total = headerBandH + tableHeaderH;
  for (const r of rows) {
    const code = r.control_code ?? "—";
    const guidance = REVIEWER_CONTROL_GUIDANCE[code];
    const ctrlTitle = guidance?.title ?? (r.question_title ?? "");
    const ctrlDesc = guidance?.reviewer_guidance ?? "";
    const status = statusFromScore(r.reviewer_score);
    const statusLabel = STATUS_STYLES[status].label;
    const observations = (r.reviewer_notes ?? "").trim() || "—";
    const recommendations = status === "implemented" ? "—" : ((r.recommended_action ?? "").trim() || "—");

    doc.setFont("helvetica", "bold"); doc.setFontSize(BODY_FONT);
    const ctrlLines = wrap(code, colW[0]);

    doc.setFont("helvetica", "normal");
    const descTitleLines = guidance && ctrlTitle ? wrap(ctrlTitle, colW[1]) : [];
    const descBodyLines = guidance && ctrlDesc ? wrap(ctrlDesc, colW[1]) : [];
    const descFallback = (guidance && ctrlTitle && ctrlDesc)
      ? []
      : wrap(ctrlTitle || ctrlDesc || (r.question_title ?? "—"), colW[1]);
    const descLines = (guidance && ctrlTitle && ctrlDesc)
      ? descTitleLines.length + descBodyLines.length
      : descFallback.length;

    doc.setFont("helvetica", "bold"); doc.setFontSize(STATUS_FONT);
    const statusMaxInner = colW[2] - CELL_PAD_X * 2;
    const statusLines = status === "partial" || status === "not_implemented"
      ? statusLabel.split(/\s+/).filter(Boolean).length
      : doc.getTextWidth(statusLabel) <= statusMaxInner
        ? 1
        : statusLabel.split(/\s+/).filter(Boolean).length;

    doc.setFont("helvetica", "normal"); doc.setFontSize(BODY_FONT);
    const obsLines = wrap(observations, colW[3]).length;
    const recLines = wrap(recommendations, colW[4]).length;

    const contentLines = Math.max(ctrlLines.length, descLines, statusLines, obsLines, recLines);
    const rowH = Math.max(28, contentLines * LINE_H + CELL_PAD_Y * 2);
    total += rowH;
  }

  return total + 6;
}

/**
 * Minimum block height that must fit on the current page before a domain may
 * begin inline. Per pagination rules, a domain is treated as a single block of
 *   [domain banner + summary + table header + first 3 data rows]
 * If that does not fit in remaining space, the entire domain starts on the
 * next page. This prevents the "header on one page, no rows" defect.
 */
function measureDomainStarterHeight(
  doc: jsPDF,
  args: {
    domain: string;
    score: CsvDomainScoreRow | null;
    controls: ControlResultRow[];
    findings: RoadmapFinding[];
    pageW: number;
  },
): number {
  const innerW = args.pageW - PAGE_MARGIN * 2;
  const scoreVal = args.score?.average_score ?? null;
  const controlsEvaluated = Number(args.score?.controls_evaluated ?? args.controls.length);
  const band = bandFromScore(scoreVal, controlsEvaluated);
  const execText = buildExecutiveAssessment(args.domain, band, args.findings, scoreVal, controlsEvaluated);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const execLines = (doc.splitTextToSize(execText, innerW - 24) as string[]).slice(0, 3);
  const headerBandH = 10 + 20 + 6 + execLines.length * 11 + 8;

  // Dedupe controls just as the renderer does.
  const seen = new Set<string>();
  const rows: ControlResultRow[] = [];
  for (const r of args.controls ?? []) {
    const k = (r.control_code ?? "").toUpperCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    rows.push(r);
    if (rows.length >= 3) break;
  }
  if (rows.length === 0) return headerBandH + 10 + 24;

  const weights = [0.11, 0.29, 0.12, 0.25, 0.23];
  const sumW = weights.reduce((a, b) => a + b, 0);
  const colW = weights.map((w) => (w / sumW) * innerW);
  const CELL_PAD_X = 5;
  const CELL_PAD_Y = 6;
  const LINE_H = 10.5;
  const BODY_FONT = 8;
  const STATUS_FONT = 8.5;
  const tableHeaderH = 11 + 10;

  const wrap = (text: string, colWidth: number): string[] => {
    const inner = colWidth - CELL_PAD_X * 2;
    const split = doc.splitTextToSize(String(text ?? "—"), inner) as string[];
    return split.length ? split : [""];
  };

  let rowsH = 0;
  for (const firstRow of rows) {
    const code = firstRow.control_code ?? "—";
    const guidance = REVIEWER_CONTROL_GUIDANCE[code];
    const ctrlTitle = guidance?.title ?? (firstRow.question_title ?? "");
    const ctrlDesc = guidance?.reviewer_guidance ?? "";
    const status = statusFromScore(firstRow.reviewer_score);
    const statusLabel = STATUS_STYLES[status].label;
    const observations = (firstRow.reviewer_notes ?? "").trim() || "—";
    const recommendations = status === "implemented" ? "—" : ((firstRow.recommended_action ?? "").trim() || "—");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(BODY_FONT);
    const ctrlLines = wrap(code, colW[0]);
    doc.setFont("helvetica", "normal");
    const descLines = guidance && ctrlTitle && ctrlDesc
      ? wrap(ctrlTitle, colW[1]).slice(0, 2).length + wrap(ctrlDesc, colW[1]).slice(0, 4).length
      : wrap(ctrlTitle || ctrlDesc || (firstRow.question_title ?? "—"), colW[1]).slice(0, 5).length;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(STATUS_FONT);
    const statusMaxInner = colW[2] - CELL_PAD_X * 2;
    const statusLines = status === "partial" || status === "not_implemented"
      ? statusLabel.split(/\s+/).filter(Boolean).length
      : doc.getTextWidth(statusLabel) <= statusMaxInner
        ? 1
        : statusLabel.split(/\s+/).filter(Boolean).length;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(BODY_FONT);
    const obsLines = wrap(observations, colW[3]).slice(0, 6).length;
    const recLines = wrap(recommendations, colW[4]).slice(0, 6).length;

    rowsH += Math.max(28, Math.max(ctrlLines.length, descLines, statusLines, obsLines, recLines) * LINE_H + CELL_PAD_Y * 2);
  }
  return headerBandH + 10 + tableHeaderH + rowsH + 6;
}

/* ─────────────────────────── Drawing helpers ──────────────────────────── */

function drawContainer(
  doc: jsPDF,
  x: number, y: number, w: number, h: number,
  fill: RGB, border: RGB,
): void {
  doc.setFillColor(...fill);
  doc.setDrawColor(...border);
  doc.setLineWidth(0.6);
  doc.roundedRect(x, y, w, h, 4, 4, "FD");
}

function drawInlineBlockLabel(doc: jsPDF, label: string, x: number, y: number): void {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...REPORT_THEME.accent.rgb);
  let cx = x;
  for (const ch of label) {
    doc.text(ch, cx, y);
    cx += doc.getTextWidth(ch) + 0.7;
  }
  doc.setDrawColor(...REPORT_THEME.gold.rgb);
  doc.setLineWidth(0.8);
  doc.line(x, y + 3, x + 22, y + 3);
}

function drawBlockListInside(
  doc: jsPDF,
  args: {
    x: number; y: number; w: number; bottom: number;
    title: string; items: string[]; glyph: string; glyphColor: RGB; emptyText: string;
  },
): void {
  drawInlineBlockLabel(doc, args.title, args.x, args.y);
  let cy = args.y + 22;

  if (args.items.length === 0) {
    drawMuted(doc, args.emptyText, args.x, cy + 4);
    return;
  }

  const lineH = 12;
  for (const raw of args.items) {
    if (cy > args.bottom - lineH) break;
    doc.setTextColor(...args.glyphColor);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(args.glyph, args.x, cy);

    doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    const split = doc.splitTextToSize(raw, args.w - 12) as string[];
    const linesAvail = Math.max(1, Math.floor((args.bottom - cy) / lineH));
    const shown = clampLines(split, Math.min(3, linesAvail));
    doc.text(shown, args.x + 12, cy, { lineHeightFactor: 1.4 } as any);
    cy += shown.length * lineH + 6;
  }
}

function drawSecondaryMetricsInline(
  doc: jsPDF,
  args: { x: number; y: number; w: number; controlsEvaluated: number; findingsLogged: number },
): void {
  const { x, y, w, controlsEvaluated, findingsLogged } = args;
  doc.setDrawColor(...REPORT_THEME.border.rgb);
  doc.setLineWidth(0.4);
  doc.line(x, y, x + w, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  const ly = y + 14;
  const left = `Controls Evaluated: `;
  doc.text(left, x, ly);
  let cx = x + doc.getTextWidth(left);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.text(String(controlsEvaluated), cx, ly);

  const right = `Findings Logged: `;
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  const rx = x + w / 2;
  doc.text(right, rx, ly);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.text(String(findingsLogged), rx + doc.getTextWidth(right), ly);
}

/* ─────────────────────────── Drawing helpers ──────────────────────────── */

function drawMuted(doc: jsPDF, text: string, x: number, y: number): void {
  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  doc.text(text, x, y);
}


function drawPriorityRow(
  doc: jsPDF,
  priority: number,
  text: string,
  x: number,
  y: number,
  innerW: number,
  bottomLimit: number,
): number {
  // Priority pill
  const label = `Priority ${priority}`;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  const padX = 6;
  const pillH = 14;
  const pillW = doc.getTextWidth(label) + padX * 2;
  doc.setFillColor(...REPORT_THEME.navy.rgb);
  doc.roundedRect(x, y - 10, pillW, pillH, pillH / 2, pillH / 2, "F");
  doc.setTextColor(255, 255, 255);
  doc.text(label, x + padX, y);

  // Action text
  const textX = x + pillW + 10;
  const textW = innerW - (pillW + 10);
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const split = doc.splitTextToSize(text, textW) as string[];
  const linesRemaining = Math.max(1, Math.floor((bottomLimit - y) / 12));
  const shown = clampLines(split, Math.min(2, linesRemaining));
  doc.text(shown, textX, y, { lineHeightFactor: 1.4 } as any);
  return y + Math.max(pillH, shown.length * 12) + 8;
}


function clampLines(lines: string[], maxLines: number): string[] {
  if (maxLines <= 0) return [];
  if (lines.length <= maxLines) return lines;
  return [
    ...lines.slice(0, maxLines - 1),
    (lines[maxLines - 1] ?? "").replace(/\.?\s*$/, "…"),
  ];
}

function riskRank(level?: string | null): number {
  return RISK_RANK[(level ?? "").toLowerCase()] ?? 9;
}

function dedupeRecs(items: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const i of items) {
    const k = i.toLowerCase().slice(0, 80);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(i);
    }
  }
  return out;
}

/* ─────────────────────────── Narrative builders ───────────────────────── */

function buildExecutiveAssessment(
  domain: string,
  band: MaturityBand,
  findings: RoadmapFinding[],
  scoreVal: number | null,
  controls: number,
): string {
  // Special case: Infrastructure Security with controls assessed but no
  // aggregate score (Partially Rated — Scope Incomplete). Use coverage-aware
  // language consistent with the chip and the Infrastructure Scope page.
  // Triggers whenever the domain has any assessed controls, regardless of
  // whether band came back Unrated or a low band — to prevent the prose
  // contradicting the "Partially Rated" chip.
  if (domain === "Infrastructure Security" && controls > 0 && (band === "Unrated" || scoreVal == null)) {
    return INFRASTRUCTURE_COVERAGE_NOTE_PDF;
  }

  const base = csvDomainNarrative(domain, scoreVal, controls);
  if (band === "Unrated") return base;

  const highSeverity = findings.filter((f) => {
    const r = (f.risk_level ?? "").toLowerCase();
    return r === "critical" || r === "high";
  }).length;

  if (findings.length === 0) {
    return `${base} No specific findings were logged against this domain during the review, suggesting controls are operating as designed at the assessed maturity level.`;
  }
  const verb = findings.length === 1 ? "was" : "were";
  const noun = findings.length === 1 ? "finding" : "findings";
  if (highSeverity > 0) {
    return `${base} ${findings.length} ${noun} ${verb} recorded against this domain, including ${highSeverity} at high or critical severity — focused remediation is warranted.`;
  }
  return `${base} ${findings.length} ${noun} ${verb} recorded against this domain, none rated above moderate severity.`;
}

/**
 * Assessment Signals — strictly data-driven facts about the domain.
 * No templated prose. Each bullet is a measurable signal sourced from:
 *   - reviewer_get_csv_domain_implementation_breakdown (counts)
 *   - calculate_csv_domain_scores (band, controls_evaluated)
 *   - reviewer_get_roadmap (findings + severity)
 */
function buildAssessmentSignals(
  _domain: string,
  band: MaturityBand,
  breakdown: DomainImplementationBreakdown | null,
  findings: RoadmapFinding[],
  controlsEvaluated: number,
): string[] {
  const out: string[] = [];

  const implemented = breakdown?.implemented ?? 0;
  const partial = breakdown?.partial ?? 0;
  const notImpl = breakdown?.not_implemented ?? 0;
  const totalRated = implemented + partial + notImpl;
  const denom = controlsEvaluated > 0 ? controlsEvaluated : totalRated;

  if (denom > 0) {
    out.push(`${implemented} of ${denom} controls fully implemented.`);
  }
  if (partial > 0) {
    out.push(`${partial} control${partial === 1 ? "" : "s"} partially implemented.`);
  }
  if (notImpl > 0) {
    out.push(`${notImpl} control${notImpl === 1 ? "" : "s"} not yet implemented.`);
  }

  const highSev = findings.filter((f) => {
    const r = (f.risk_level ?? "").toLowerCase();
    return r === "critical" || r === "high";
  }).length;
  if (findings.length > 0) {
    out.push(
      `${findings.length} finding${findings.length === 1 ? "" : "s"} logged` +
        (highSev > 0 ? ` · ${highSev} high or critical.` : "."),
    );
  } else if (denom > 0) {
    out.push("No findings logged against this domain.");
  }

  if (band !== "Unrated") {
    out.push(`Maturity rated ${BAND_LABEL[band]}.`);
  }

  return out.slice(0, 5);
}

function buildGapBullets(
  findings: RoadmapFinding[],
  breakdown: DomainImplementationBreakdown | null,
): string[] {
  const out: string[] = [];

  // Top findings by risk rank
  const sorted = [...findings].sort((a, b) => riskRank(a.risk_level) - riskRank(b.risk_level));
  for (const f of sorted) {
    if (out.length >= 3) break;
    const risk = (f.risk_level ?? "").toLowerCase();
    if (risk === "low") continue;
    const title = (f.finding_title ?? "").trim();
    if (!title) continue;
    out.push(`${title} (${f.risk_level}).`);
  }

  // Supplement with implementation gap counts if needed
  if (out.length < 3) {
    const partial = breakdown?.partial ?? 0;
    const notImpl = breakdown?.not_implemented ?? 0;
    if (notImpl > 0) {
      out.push(`${notImpl} control${notImpl === 1 ? "" : "s"} not yet implemented.`);
    }
    if (out.length < 3 && partial > 0) {
      out.push(`${partial} control${partial === 1 ? "" : "s"} only partially implemented.`);
    }
  }
  return out.slice(0, 4);
}

function buildRiskImplication(
  domain: string,
  band: MaturityBand,
  findings: RoadmapFinding[],
): string {
  const highSev = findings.filter((f) => {
    const r = (f.risk_level ?? "").toLowerCase();
    return r === "critical" || r === "high";
  }).length;
  const dl = domain.toLowerCase();

  if (band === "Unrated") {
    return `Until ${dl} controls are formally rated, residual risk exposure in this area cannot be quantified and may be obscured from executive oversight.`;
  }
  if (band === "Minimal") {
    return `The current minimal maturity in ${dl} materially increases the likelihood of control failure, prolonged incident impact, and limited management visibility over emerging risks.`;
  }
  if (band === "Adequate") {
    if (highSev > 0) {
      return `Although ${dl} is operating at an adequate level overall, ${highSev} high-severity finding${highSev === 1 ? "" : "s"} indicate${highSev === 1 ? "s" : ""} concentrated residual risk that warrants targeted remediation.`;
    }
    return `Maturity in ${dl} is adequate; however, partially implemented controls leave residual risk that could be exploited under sustained pressure or incident conditions.`;
  }
  if (band === "Strong") {
    if (highSev > 0) {
      return `Overall maturity in ${dl} is strong, but ${highSev} elevated-risk finding${highSev === 1 ? "" : "s"} represent${highSev === 1 ? "s" : ""} concentrated exposure that should be remediated to preserve assurance.`;
    }
    return `Strong maturity in ${dl} provides resilient coverage; residual risk is limited but should continue to be monitored through periodic reassessment.`;
  }
  return `Extensive maturity in ${dl} indicates capabilities are operating at a leading practice level. Residual risk is low and should be sustained through continuous assurance.`;
}

/* ─────────── Phase 1.8 — Infrastructure Scope Disclosure ─────────── */

function hasInfrastructureScope(scope?: InfrastructureScopeForReport | null): boolean {
  if (!scope) return false;
  return (
    (scope.telecom_service_model ?? []).length > 0 ||
    (scope.components_present ?? []).length > 0 ||
    (scope.components_assessed ?? []).length > 0 ||
    !!(scope.scope_notes && scope.scope_notes.trim())
  );
}

function computeCoveragePct(scope: InfrastructureScopeForReport | null): number | null {
  if (!scope) return null;
  const present = scope.components_present ?? [];
  const assessed = scope.components_assessed ?? [];
  if (present.length === 0) return null;
  const assessedInPresent = assessed.filter((c) => present.includes(c)).length;
  return Math.round((assessedInPresent / present.length) * 100);
}

function renderInfrastructureScopePage(
  doc: jsPDF,
  args: {
    scope: InfrastructureScopeForReport;
    clientOrgName: string;
    methodology?: string | null;
    frameworks?: string[] | null;
    pageNumber: number;
  },
): void {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const innerW = pageW - PAGE_MARGIN * 2;

  doc.addPage();
  drawRunningHeader(doc, {
    clientName: args.clientOrgName,
    frameworks: args.frameworks ?? null,
    methodology: args.methodology ?? null,
  });
  let y = drawSectionTitle(doc, {
    sectionNumber: "05",
    title: "Assessment Results by Cybersecurity Domain",
    continued: true,
  });

  // Title
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Infrastructure Assessment Scope", PAGE_MARGIN, y + 16);
  y += 32;

  // Intro — coverage gaps directly affect Infrastructure Security scoring.
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  const intro =
    "The Infrastructure Assessment Scope below provides transparency into the telecom service models and infrastructure components included in this engagement. Scope directly affects Infrastructure Security maturity scoring when infrastructure controls are assessed. Components outside the assessed scope do not contribute to the Infrastructure Security maturity calculation, and coverage gaps should be considered when interpreting the domain rating.";
  const introLines = doc.splitTextToSize(intro, innerW) as string[];
  doc.text(introLines, PAGE_MARGIN, y, { lineHeightFactor: 1.5 } as any);
  y += introLines.length * 14 + 16;

  const scope = args.scope;
  const models = scope.telecom_service_model ?? [];
  const present = scope.components_present ?? [];
  const assessed = scope.components_assessed ?? [];
  const coverage = computeCoveragePct(scope);
  const assessedSet = new Set(assessed.map((c) => c.toLowerCase()));

  // ── Infrastructure Coverage Summary — KPI cards ──
  {
    const kpiCardH = 64;
    const kpiGap = 10;
    const kpiW = (innerW - kpiGap * 3) / 4;
    const scopeStatus =
      coverage === null
        ? "—"
        : coverage >= 100
          ? "Complete"
          : coverage > 0
            ? "Partial"
            : "Incomplete";
    const kpis: Array<{ label: string; value: string; tone?: RGB }> = [
      { label: "COMPONENTS PRESENT", value: String(present.length) },
      { label: "COMPONENTS ASSESSED", value: String(assessed.length) },
      { label: "COVERAGE", value: coverage !== null ? `${coverage}%` : "—" },
      { label: "SCOPE STATUS", value: scopeStatus, tone: scopeStatus === "Complete" ? [22, 163, 74] : [217, 119, 6] },
    ];
    kpis.forEach((k, i) => {
      const kx = PAGE_MARGIN + i * (kpiW + kpiGap);
      drawContainer(doc, kx, y, kpiW, kpiCardH, REPORT_THEME.surfaceAlt.rgb, REPORT_THEME.border.rgb);
      doc.setFillColor(...REPORT_THEME.gold.rgb);
      doc.rect(kx, y, 3, kpiCardH, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(...REPORT_THEME.textMuted.rgb);
      doc.text(k.label, kx + 10, y + 16);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      doc.setTextColor(...(k.tone ?? REPORT_THEME.navy.rgb));
      doc.text(k.value, kx + 10, y + 46);
    });
    y += kpiCardH + 8;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8.5);
    doc.setTextColor(...REPORT_THEME.textMuted.rgb);
    doc.text(
      "Coverage gaps reduce Infrastructure Security's achievable maturity rating.",
      PAGE_MARGIN,
      y + 4,
    );
    y += 18;
  }

  // ── Telecom Service Models — badges ──
  if (y + 50 <= pageH - 90) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...REPORT_THEME.accent.rgb);
    doc.text("TELECOM SERVICE MODELS", PAGE_MARGIN, y + 12);
    y += 22;

    if (models.length === 0) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(10);
      doc.setTextColor(...REPORT_THEME.textMuted.rgb);
      doc.text("—", PAGE_MARGIN, y + 4);
      y += 18;
    } else {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      const badgeH = 18;
      const padX = 10;
      const gap = 8;
      let bx = PAGE_MARGIN;
      let by = y;
      for (const m of models) {
        const label = String(m);
        const tw = doc.getTextWidth(label);
        const bw = tw + padX * 2;
        if (bx + bw > PAGE_MARGIN + innerW) {
          bx = PAGE_MARGIN;
          by += badgeH + gap;
        }
        // Soft accent badge: light fill + accent border + accent text
        doc.setFillColor(238, 244, 250);
        doc.setDrawColor(...REPORT_THEME.accent.rgb);
        doc.setLineWidth(0.7);
        doc.roundedRect(bx, by, bw, badgeH, badgeH / 2, badgeH / 2, "FD");
        doc.setTextColor(...REPORT_THEME.accent.rgb);
        doc.text(label, bx + padX, by + 12);
        bx += bw + gap;
      }
      y = by + badgeH + 14;
    }
  }

  // ── Infrastructure Components table ──
  if (present.length > 0 && y + 60 <= pageH - 90) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...REPORT_THEME.accent.rgb);
    doc.text("INFRASTRUCTURE COMPONENTS", PAGE_MARGIN, y + 12);
    y += 20;

    const colCompW = innerW * 0.66;
    const colPresentW = innerW * 0.17;
    const colAssessedW = innerW * 0.17;
    const headerH = 22;

    // Header (lighter accent blue)
    doc.setFillColor(...REPORT_THEME.accent.rgb);
    doc.rect(PAGE_MARGIN, y, innerW, headerH, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text("INFRASTRUCTURE COMPONENT", PAGE_MARGIN + 10, y + 14);
    doc.text("PRESENT", PAGE_MARGIN + colCompW + 10, y + 14);
    doc.text("ASSESSED", PAGE_MARGIN + colCompW + colPresentW + 10, y + 14);
    y += headerH;

    const rowH = 22;
    present.forEach((comp, idx) => {
      if (y + rowH > pageH - 90) return;
      if (idx % 2 === 1) {
        doc.setFillColor(247, 249, 252);
        // Inset so the bottom divider of the prior row is not masked.
        doc.rect(PAGE_MARGIN, y + 1, innerW, rowH - 2, "F");
      }
      doc.setDrawColor(...REPORT_THEME.border.rgb);
      doc.setLineWidth(0.4);
      doc.line(PAGE_MARGIN, y + rowH, PAGE_MARGIN + innerW, y + rowH);
      doc.line(PAGE_MARGIN, y, PAGE_MARGIN + innerW, y);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
      doc.text(String(comp), PAGE_MARGIN + 10, y + 14);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.setTextColor(22, 163, 74); // green
      doc.text("Yes", PAGE_MARGIN + colCompW + 10, y + 14);

      const isAssessed = assessedSet.has(String(comp).toLowerCase());
      if (isAssessed) {
        doc.setTextColor(22, 163, 74);
        doc.text("Yes", PAGE_MARGIN + colCompW + colPresentW + 10, y + 14);
      } else {
        doc.setTextColor(217, 119, 6);
        doc.text("No", PAGE_MARGIN + colCompW + colPresentW + 10, y + 14);
      }
      y += rowH;
    });
    y += 12;
  }

  // Scope Notes & overflow disclaimer were relocated to "Appendix A —
  // Infrastructure Assessment Scope Notes" to keep Section 05 visually focused
  // on assessment results rather than long-form scope narrative.


  drawPageFooter(doc, args.pageNumber);
}


